import { useMutation, useQueryClient, UseMutationResult } from "@tanstack/react-query";
import axios from "axios";
import { HeadersConfig } from "../../types/auth";
import { getConfigForRegion, ApiSystemData, PresignedUrlResponse } from "../../config";
import { systemsKeys } from "./queryKeys";
import { findRegionByDisplayName } from "./utils";

export interface DeleteSystemPayload {
    id: string;
    region: string;
}

export interface CreateSystemPayload {
    systems: Pick<ApiSystemData, "id" | "companyId" | "name">[];
    region: string;
}

export interface UpdateSystemPayload {
    id: string;
    companyId: number;
    region: string;
}

export interface RegenerateCertPayload {
    id: string;
    region: string;
}

export interface DownloadCertPayload {
    id: string;
    region: string;
}

export const useDeleteSystemMutation = (
    headers?: HeadersConfig
): UseMutationResult<void, Error, DeleteSystemPayload> => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, DeleteSystemPayload>({
        mutationFn: async ({ id, region }) => {
            try {
                console.log(`Deleting system ${id} in region ${region}`);

                const selectedRegion = findRegionByDisplayName(region);
                const regionConfig = getConfigForRegion(selectedRegion);

                const res = await axios.post(regionConfig.api.DELETE_SYSTEMS, { id }, headers);

                if (res.status !== 200) {
                    throw new Error("Failed to delete system");
                }
            } catch (error) {
                console.error(`Error deleting system ${id} in region ${region}:`, error);
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: systemsKeys.all });
        }
    });
};

export const useCreateSystemMutation = (
    headers: HeadersConfig
): UseMutationResult<void, Error, CreateSystemPayload> => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, CreateSystemPayload>({
        mutationFn: async ({ systems, region }) => {
            try {
                console.log(`Creating systems in region ${region}:`, systems);

                const selectedRegion = findRegionByDisplayName(region);
                const regionConfig = getConfigForRegion(selectedRegion);

                const res = await axios.post(regionConfig.api.CREATE_SYSTEMS, { systems }, headers);

                if (res.status !== 200) {
                    throw new Error("Failed to create system");
                }
            } catch (error) {
                console.error(`Error creating systems in region ${region}:`, error);
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: systemsKeys.all });
        }
    });
};

export const useUpdateSystemMutation = (
    headers: HeadersConfig
): UseMutationResult<void, Error, UpdateSystemPayload> => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, UpdateSystemPayload>({
        mutationFn: async ({ id, companyId, region }) => {
            try {
                console.log(`Updating system ${id} to company ${companyId} in region ${region}`);

                const selectedRegion = findRegionByDisplayName(region);
                const regionConfig = getConfigForRegion(selectedRegion);

                const res = await axios.post(regionConfig.api.UPDATE_SYSTEMS, { id, companyId }, headers);

                if (res.status !== 200) {
                    throw new Error("Failed to update system");
                }
            } catch (error) {
                console.error(`Error updating system ${id} in region ${region}:`, error);
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: systemsKeys.all });
        }
    });
};

export const useRegenerateCertMutation = (
    headers?: HeadersConfig
): UseMutationResult<void, Error, RegenerateCertPayload> => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, RegenerateCertPayload>({
        mutationFn: async ({ id, region }) => {
            try {
                console.log(`Regenerating certificate for system ${id} in region ${region}`);

                const selectedRegion = findRegionByDisplayName(region);
                const regionConfig = getConfigForRegion(selectedRegion);

                const res = await axios.post(regionConfig.api.SYSTEM_REGENERATE_CERTS, { id }, headers);

                if (res.status !== 200 || res.data !== true) {
                    throw new Error("Failed to regenerate certificate");
                }
            } catch (error) {
                console.error(`Error regenerating certificate for system ${id} in region ${region}:`, error);
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: systemsKeys.all });
        }
    });
};

export const useDownloadCertMutation = (
    headers?: HeadersConfig
): UseMutationResult<PresignedUrlResponse[], Error, DownloadCertPayload> => {
    return useMutation<PresignedUrlResponse[], Error, DownloadCertPayload>({
        mutationFn: async ({ id, region }) => {
            try {
                console.log(`Downloading certificate for system ${id} in region ${region}`);

                const selectedRegion = findRegionByDisplayName(region);
                const regionConfig = getConfigForRegion(selectedRegion);

                const payload = { systems: [{ id }] };
                const res = await axios.post<PresignedUrlResponse[]>(regionConfig.api.SYSTEM_CERTS, payload, headers);

                if (res.status !== 200 || !res.data[0].success || !res.data[0].url) {
                    throw new Error(res.data[0].error || "Failed to retrieve certificate URL");
                }

                return res.data;
            } catch (error) {
                console.error(`Error downloading certificate for system ${id} in region ${region}:`, error);
                throw error;
            }
        }
    });
};
